import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, KeyRound } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, Panel } from "../components/shared";
import { AuthContextUser } from "../components/AuthGate";

function AccountRow({ u, onChange }) {
  const isSuper = u.role === "super_admin";
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid={`access-row-${u.email}`} className={`flex flex-wrap items-center gap-3 p-4 border-b border-[#1E293B] last:border-0 ${u.active === false ? "opacity-50" : ""}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="font-semibold">{u.email}</div>
        <div className="font-mono text-[11px] text-[#6B7280]">{u.name} · {u.totp_enabled ? "2FA SET UP" : "2FA PENDING"}{u.last_login_at ? ` · LAST LOGIN ${u.last_login_at.slice(0, 10)}` : " · NEVER SIGNED IN"}</div>
      </div>
      <span data-testid={`access-role-${u.email}`} className={`font-mono text-[10px] font-bold px-2 py-1 rounded-sm ${isSuper ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "bg-[#172033] text-[#9CA3AF]"}`}>{isSuper ? "SUPER ADMIN" : "MEMBER"}</span>
      {!isSuper && (<>
        <button data-testid={`access-toggle-${u.email}`} onClick={() => act(() => api.patch(`/admin/access/${u.user_id}`, { active: u.active === false }), u.active === false ? "Access restored" : "Access paused")}
          className="h-10 px-3 rounded-md border border-[#334155] text-xs font-mono">{u.active === false ? "ENABLE" : "DISABLE"}</button>
        <button data-testid={`access-reset2fa-${u.email}`} title="Reset authenticator" onClick={() => window.confirm(`Reset 2FA for ${u.email}? They will scan a new QR code at next sign-in.`) && act(() => api.post(`/admin/access/${u.user_id}/reset-2fa`), "2FA reset")}
          className="h-10 w-10 grid place-items-center rounded-md border border-[#334155]"><KeyRound className="w-4 h-4" /></button>
        <button data-testid={`access-remove-${u.email}`} title="Remove access" onClick={() => window.confirm(`Remove ${u.email} from the access list?`) && act(() => api.delete(`/admin/access/${u.user_id}`), "Access removed")}
          className="h-10 w-10 grid place-items-center rounded-md border border-[#EF4444]/50 text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
      </>)}
    </div>
  );
}

export default function SuperAdmin() {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const load = () => api.get("/admin/access").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (AuthContextUser.current?.is_super_admin) load(); }, []);
  if (!AuthContextUser.current?.is_super_admin) return <Panel className="p-8 text-[#9CA3AF]" data-testid="super-admin-denied">Super-admin only.</Panel>;
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/access", { email, name }); toast.success(`${email} can now sign in`); setEmail(""); setName(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div data-testid="super-admin-page">
      <PageHeader kicker="// ACCESS WHITELIST" title="Super Admin" />
      <Panel className="p-5 mb-6">
        <div className="flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-[#F59E0B] mb-3"><ShieldCheck className="w-4 h-4" />ADD ACCOUNT</div>
        <p className="text-sm text-[#9CA3AF] mb-4">Only Google accounts on this list can sign in. Added accounts are members; super-admin stays with john@wellsmadecompany.com only.</p>
        <form onSubmit={add} className="flex flex-wrap gap-2">
          <input data-testid="access-email-input" type="email" required placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 flex-1 min-w-[220px] px-3 bg-[#090D16] border border-[#334155] rounded-md" />
          <input data-testid="access-name-input" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="h-12 w-48 px-3 bg-[#090D16] border border-[#334155] rounded-md" />
          <button data-testid="access-add-btn" type="submit" className="h-12 px-5 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center gap-2"><UserPlus className="w-4 h-4" />Whitelist</button>
        </form>
      </Panel>
      <Panel data-testid="access-list">{users.map((u) => <AccountRow key={u.user_id} u={u} onChange={load} />)}</Panel>
    </div>
  );
}
